import { BaseURL } from "@/config/apiUrl";
import { signOutRequest } from "@/store/auth/authSlice";
import classNames from "classnames";
import { formRegEx, formRegExReplacer } from "constant/regex";
import moment from "moment";
import { toast } from "react-toastify";
import { CURRENCY } from "../constant/constants";

export const apiHeader = (token, isFormData) => {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    "timezone-offset": moment().utcOffset(),
    "Content-Type": isFormData ? "multipart/form-data" : "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return { headers };
};

export const getUserName = (data) => {
  if (!data) return "";

  const firstName = data?.firstName || data?.user?.firstName;
  const lastName = data?.lastName || data?.user?.lastName;

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName || lastName || "";
};

export const formatMessageDate = (date) => {
  const messageDate = moment(date);
  const now = moment();

  if (messageDate.isSame(now, "minute")) {
    // If the date is exactly now, show the current time, e.g., 'HH:mm A'
    return now.format("HH:mm A");
  } else if (messageDate.isSame(now, "day")) {
    // If the date is today, use fromNow()
    return messageDate.fromNow();
  } else if (messageDate.isSame(now.subtract(1, "day"), "day")) {
    // If the date is yesterday, use fromNow()
    return messageDate.fromNow();
  } else {
    // Otherwise, show the date in a standard format, e.g., 'DD/MM/YYYY'
    return messageDate.format("DD/MM/YYYY");
  }
};

export const formatDate = (date) => {
  const formattedDate = moment(date);
  return formattedDate.format("DD/MM/YYYY");
};

export const formatTime = (time) => {
  const formattedTime = moment(time);
  return formattedTime.format("hh:mm A");
};

export const scrollToTop = (topVal = 0) => {
  window.scrollTo({
    top: topVal,
    behavior: "smooth",
  });
};

export const formatNumber = (num) => {
  if (num % 1 === 0) {
    return `${CURRENCY}${num?.toLocaleString()}`;
  } else {
    return `${CURRENCY}${num?.toFixed(2)?.toLocaleString()}`;
  }
};

export const quillValidateHandler = (
  params,
  toastMessage = "Please fill the home cms field!",
) => {
  let flag = true;
  for (let key in params) {
    const parsedHTML = new DOMParser().parseFromString(
      params[key],
      "text/html",
    );
    const plainText = parsedHTML.body.textContent || "";
    if (plainText?.trim() === "") {
      toast.error(toastMessage);
      flag = false;
      break;
    }
  }
  return flag;
};

export const cn = (...args) => classNames(...args);

export const validateUrl = (url) => {
  const urlRegex =
    /^(https?:\/\/)([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  return urlRegex.test(url);
};

export const camelCaseToLower = (str) => {
  return str.replace(formRegEx, formRegExReplacer).toLowerCase();
};

export const urlPattern =
  /^(https?:\/\/)(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?::\d{1,5})?(?:\/[^\s?#]*)?(?:\?[^\s#]*)?(?:#[^\s]*)?$/;

export const getYearRange = () => {
  const years = [
    ...Array.from({ length: 5 }).map((_, i) => ({
      label: `${moment().subtract(i, "year").format("YYYY")}`,
      value: `${moment().subtract(i, "year").format("YYYY")}`,
    })),
  ];
  return years;
};

export const RenderToast = ({ type = "error", message = "" }) => {
  let toastId = "error-render-toast";

  if (!type || !message) return;

  toast[type](message, {
    id: toastId,
  });
};

/**
 * Validates if all days in all months are filled with band selections
 * @param {Array} calendarData - Array of month objects with days array
 * @param {string} customMessage - Custom error message (optional)
 * @returns {boolean} - Returns true if all days are filled, false otherwise
 */

export const validateNestedParams = (
  params,
  parentKey = "",
  options = { disallowEmptyArray: true },
) => {
  const formatKey = (rawKey) => {
    return rawKey
      .replace(/\[(\d+)\]/g, (_, i) => ` ${parseInt(i) + 1}`) // [0] => 1
      .split(".")
      .map(
        (part) =>
          part
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase()), // capitalize
      )
      .join(" → ");
  };

  if (Array.isArray(params)) {
    if (options.disallowEmptyArray && params.length === 0) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (let i = 0; i < params.length; i++) {
      const key = `${parentKey}[${i}]`;
      if (!validateNestedParams(params[i], key, options)) return false;
    }
  } else if (typeof params === "object" && params !== null) {
    const keys = Object.keys(params);
    if (keys.length === 0 && parentKey) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }

    for (const key of keys) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      if (!validateNestedParams(params[key], fullKey, options)) return false;
    }
  } else {
    if (params === "" || params === null || params === undefined) {
      toast.error(`${formatKey(parentKey)} cannot be empty`);
      return false;
    }
  }

  return true;
};

export const validateEmail = (email) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const getUserFullName = (user) => {
  if (!user) return "";
  if (user?.firstName && user?.lastName) {
    return `${user?.firstName} ${user?.lastName}`;
  }
  if (user?.firstName) {
    return user?.firstName;
  }
  if (user?.lastName) {
    return user?.lastName;
  }
  return "";
};

export const getYearOptions = () => {
  return Array.from({ length: 5 }).map((_, i) => ({
    label: `${moment().subtract(i, "year").format("YYYY")}`,
    value: `${moment().subtract(i, "year").format("YYYY")}`,
  }));
};

export const getOptionValue = (options, value, key = "value") => {
  if (!options || options?.length === 0) return null;
  const foundOption = options?.find((option) => option?.[key] === value);
  if (foundOption) {
    return foundOption;
  }
  return options?.[0];
};

export const setQueryString = (params, url = "") => {
  const baseUrl = BaseURL(url);
  return baseUrl + "?" + new URLSearchParams(params).toString();
};
export const truncate = (text, maxLength = 50, suffix = "...") => {
  if (!text || typeof text !== "string") return text || "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + suffix;
};

export const getMonthOptions = () => {
  return [
    {
      label: "Jan",
      value: 1,
    },
    {
      label: "Feb",
      value: 2,
    },
    {
      label: "Mar",
      value: 3,
    },
    {
      label: "Apr",
      value: 4,
    },
    {
      label: "May",
      value: 5,
    },
    {
      label: "Jun",
      value: 6,
    },
    {
      label: "Jul",
      value: 7,
    },
    {
      label: "Aug",
      value: 8,
    },
    {
      label: "Sep",
      value: 9,
    },
    {
      label: "Oct",
      value: 10,
    },
    {
      label: "Nov",
      value: 11,
    },
    {
      label: "Dec",
      value: 12,
    },
  ];
};

// get date form  current moment - days
export const getDateFromCurrentMoment = (days) => {
  return moment().subtract(days, "days").format("DD/MM/YY");
};

export const handleSignOut = (dispatch, navigate) => {
  dispatch(signOutRequest());
  navigate("/login");
};
